/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Phone, Calendar, MapPin, Users, Loader2, FileText, Send, Trash2, Archive, CheckCircle2, MessageSquare, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { adminQuotesApi, formatApiError } from "@/lib/api";

const STATUS_LABEL = {
  new: "Nouveau",
  contacted: "Contacté",
  converted: "Devis créé",
  archived: "Archivé",
};

const STATUS_COLOR = {
  new: "text-[#DCB457] border-[#DCB457]/50 bg-[#DCB457]/10",
  contacted: "text-sky-300 border-sky-400/50 bg-sky-500/10",
  converted: "text-emerald-300 border-emerald-400/50 bg-emerald-500/10",
  archived: "text-white/45 border-white/15 bg-white/5",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

export default function AdminQuotes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    adminQuotesApi.list(statusFilter || undefined)
      .then(setItems)
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line

  const updateStatus = async (id, status) => {
    setBusy(true);
    try {
      const updated = await adminQuotesApi.updateStatus(id, { status });
      setItems((xs) => xs.map((x) => (x.id === id ? updated : x)));
      if (selected?.id === id) setSelected(updated);
      toast.success("Statut mis à jour.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const convertToDevis = async (id) => {
    setBusy(true);
    try {
      const devis = await adminQuotesApi.convertToDevis(id);
      toast.success(`Devis ${devis.number} créé — vous pouvez le compléter.`);
      await updateStatus(id, "converted");
      navigate(`/admin/devis/${devis.id}`);
    } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };

  const deleteQuote = async (id) => {
    if (!window.confirm("Supprimer définitivement cette demande ?")) return;
    setBusy(true);
    try {
      await adminQuotesApi.remove(id);
      setItems((xs) => xs.filter((x) => x.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Demande supprimée.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const counts = items.reduce((acc, q) => {
    const s = q.status || "new";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  return (
    <div data-testid="admin-quotes-page">
      <header className="mb-8">
        <p className="eyebrow">Administration</p>
        <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3">
          Demandes de <span className="italic gold-text">devis.</span>
        </h1>
        <p className="text-white/60 text-sm md:text-base mt-3 max-w-2xl">
          Chaque demande reçue via le formulaire de contact — répondez, convertissez en devis chiffré, archivez.
        </p>
      </header>

      {/* Status filters */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-quotes-filters">
        <FilterChip active={!statusFilter} onClick={() => setStatusFilter("")} testid="filter-all">
          Toutes ({items.length})
        </FilterChip>
        {Object.entries(STATUS_LABEL).map(([k, label]) => (
          <FilterChip
            key={k}
            active={statusFilter === k}
            onClick={() => setStatusFilter(k)}
            testid={`filter-${k}`}
          >
            {label} ({counts[k] || 0})
          </FilterChip>
        ))}
      </div>

      {loading ? (
        <p className="text-white/55 italic flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Chargement…</p>
      ) : items.length === 0 ? (
        <div className="card-luxe p-12 text-center" data-testid="admin-quotes-empty">
          <FileText size={36} strokeWidth={1} className="text-[#DCB457] mx-auto mb-4" />
          <p className="text-white/65">Aucune demande {statusFilter ? `avec le statut « ${STATUS_LABEL[statusFilter]} »` : ""} pour le moment.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* List */}
          <ul className="lg:col-span-2 space-y-3 max-h-[80vh] overflow-y-auto pr-1" data-testid="admin-quotes-list">
            {items.map((q) => (
              <li key={q.id}>
                <button
                  onClick={() => setSelected(q)}
                  data-testid={`admin-quote-item-${q.id}`}
                  className={`w-full text-left card-luxe p-4 hover:border-[#DCB457]/40 transition ${selected?.id === q.id ? "border-[#DCB457]/60 bg-[#DCB457]/5" : ""}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-serif-luxe text-lg leading-tight truncate">{q.nom || q.prenom || "—"}</p>
                      <p className="text-xs text-white/50 truncate">{q.email}</p>
                    </div>
                    <span className={`text-[9px] uppercase tracking-[0.14em] border px-2 py-0.5 whitespace-nowrap ${STATUS_COLOR[q.status || "new"]}`}>
                      {STATUS_LABEL[q.status || "new"]}
                    </span>
                  </div>
                  <div className="text-xs text-white/60 flex flex-wrap gap-x-3 gap-y-1">
                    <span className="text-[#DCB457]">{q.type_evenement}</span>
                    {q.date_evenement && <span>· {q.date_evenement}</span>}
                    {q.ville && <span>· {q.ville}</span>}
                  </div>
                  <p className="text-[10px] text-white/40 mt-2">{fmtDate(q.created_at)}</p>
                </button>
              </li>
            ))}
          </ul>

          {/* Detail panel */}
          <div className="lg:col-span-3">
            {selected ? (
              <QuoteDetail
                q={selected}
                busy={busy}
                onStatus={updateStatus}
                onConvert={convertToDevis}
                onDelete={deleteQuote}
              />
            ) : (
              <div className="card-luxe p-10 text-center h-full flex flex-col items-center justify-center">
                <MessageSquare size={40} strokeWidth={1} className="text-[#DCB457]/60 mx-auto mb-4" />
                <p className="text-white/50">Sélectionnez une demande à gauche pour voir les détails.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-[11px] uppercase tracking-[0.16em] px-3 py-1.5 border transition ${
        active ? "bg-[#DCB457] text-black border-[#DCB457]" : "text-white/60 border-[#DCB457]/30 hover:border-[#DCB457]"
      }`}
    >
      {children}
    </button>
  );
}

function QuoteDetail({ q, busy, onStatus, onConvert, onDelete }) {
  const fullAddress = [q.adresse, q.code_postal, q.ville].filter(Boolean).join(", ");
  return (
    <div className="card-luxe p-6 md:p-8 space-y-6" data-testid={`admin-quote-detail-${q.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="eyebrow">{q.type_evenement}</p>
          <h2 className="font-serif-luxe text-2xl md:text-3xl mt-2">{q.nom}</h2>
          <p className="text-white/50 text-xs mt-1">Reçu le {fmtDate(q.created_at)}</p>
        </div>
        <span className={`text-[10px] uppercase tracking-[0.16em] border px-2.5 py-1 ${STATUS_COLOR[q.status || "new"]}`}>
          {STATUS_LABEL[q.status || "new"]}
        </span>
      </div>

      {/* Coords */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <a href={`mailto:${q.email}`} className="flex items-start gap-2.5 text-white/80 hover:text-[#DCB457]" data-testid="admin-quote-email">
          <Mail size={15} strokeWidth={1.4} className="text-[#DCB457] mt-0.5" />
          <span className="break-all">{q.email}</span>
        </a>
        <a href={`tel:${q.telephone}`} className="flex items-start gap-2.5 text-white/80 hover:text-[#DCB457]" data-testid="admin-quote-phone">
          <Phone size={15} strokeWidth={1.4} className="text-[#DCB457] mt-0.5" />
          {q.telephone}
        </a>
        {q.date_evenement && (
          <div className="flex items-start gap-2.5 text-white/80">
            <Calendar size={15} strokeWidth={1.4} className="text-[#DCB457] mt-0.5" />
            {q.date_evenement}{q.horaires ? ` · ${q.horaires}` : ""}
          </div>
        )}
        {q.nb_invites && (
          <div className="flex items-start gap-2.5 text-white/80">
            <Users size={15} strokeWidth={1.4} className="text-[#DCB457] mt-0.5" />
            {q.nb_invites} invités
          </div>
        )}
        {(fullAddress || q.lieu) && (
          <div className="flex items-start gap-2.5 text-white/80 md:col-span-2">
            <MapPin size={15} strokeWidth={1.4} className="text-[#DCB457] mt-0.5" />
            <div>
              {fullAddress && <div>{fullAddress}</div>}
              {q.lieu && <div className="text-white/60 text-xs italic">Lieu événement : {q.lieu}</div>}
            </div>
          </div>
        )}
      </div>

      {q.forfait_nom && (
        <div className="bg-[#DCB457]/6 border border-[#DCB457]/25 p-4">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#DCB457] mb-1">Forfait souhaité</p>
          <p className="text-white/85">{q.forfait_nom}</p>
        </div>
      )}

      {q.prestations?.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#DCB457] mb-2">Prestations</p>
          <div className="flex flex-wrap gap-1.5">
            {q.prestations.map((p, i) => (
              <span key={i} className="text-[11px] px-2.5 py-1 border border-[#DCB457]/25 text-white/75">{p}</span>
            ))}
          </div>
        </div>
      )}

      {q.message && (
        <div className="bg-black/50 border-l-2 border-[#DCB457] p-4">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#DCB457] mb-2">Message</p>
          <p className="text-white/80 text-sm whitespace-pre-wrap">{q.message}</p>
        </div>
      )}

      {q.linked_devis && (
        <div className="bg-emerald-500/5 border border-emerald-400/25 p-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-emerald-300 mb-1">Devis lié</p>
            <p className="font-mono text-sm">{q.linked_devis.number} · {Number(q.linked_devis.total_ttc || 0).toFixed(2)} €</p>
          </div>
          <Link to={`/admin/devis/${q.linked_devis.id}`} data-testid="admin-quote-linked-devis" className="text-[11px] uppercase tracking-[0.18em] text-emerald-300 border border-emerald-400/40 px-3 py-1.5 hover:bg-emerald-500/10 inline-flex items-center gap-1.5">
            Ouvrir <ExternalLink size={11} strokeWidth={1.5} />
          </Link>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2 pt-4 border-t border-[#DCB457]/15">
        <a
          href={`mailto:${q.email}?subject=Votre demande My Animation&body=Bonjour ${q.prenom || q.nom || ""},%0D%0A%0D%0A`}
          data-testid="admin-quote-reply"
          className="text-[11px] uppercase tracking-[0.18em] bg-[#DCB457] text-black px-3 py-2 hover:bg-[#E5C24A] inline-flex items-center gap-1.5"
        >
          <Send size={11} strokeWidth={1.5} /> Répondre par email
        </a>
        {!q.linked_devis && (
          <button
            onClick={() => onConvert(q.id)}
            disabled={busy}
            data-testid="admin-quote-convert"
            className="text-[11px] uppercase tracking-[0.18em] text-[#DCB457] border border-[#DCB457]/50 px-3 py-2 hover:bg-[#DCB457]/10 inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <FileText size={11} strokeWidth={1.5} /> Créer un devis
          </button>
        )}
        {(q.status || "new") !== "contacted" && (
          <button
            onClick={() => onStatus(q.id, "contacted")}
            disabled={busy}
            data-testid="admin-quote-mark-contacted"
            className="text-[11px] uppercase tracking-[0.18em] text-sky-300 border border-sky-400/50 px-3 py-2 hover:bg-sky-500/10 inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <CheckCircle2 size={11} strokeWidth={1.5} /> Marquer contacté
          </button>
        )}
        {(q.status || "new") !== "archived" && (
          <button
            onClick={() => onStatus(q.id, "archived")}
            disabled={busy}
            data-testid="admin-quote-archive"
            className="text-[11px] uppercase tracking-[0.18em] text-white/60 border border-white/20 px-3 py-2 hover:bg-white/5 inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <Archive size={11} strokeWidth={1.5} /> Archiver
          </button>
        )}
        <button
          onClick={() => onDelete(q.id)}
          disabled={busy}
          data-testid="admin-quote-delete"
          className="text-[11px] uppercase tracking-[0.18em] text-red-300 border border-red-400/40 px-3 py-2 hover:bg-red-500/10 inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          <Trash2 size={11} strokeWidth={1.5} /> Supprimer
        </button>
      </div>
    </div>
  );
}
